import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import Step1PersonalInfo from './form-steps/Step1PersonalInfo';
import Step2EmergencyContact from './form-steps/Step2EmergencyContact';
import Step3Eligibility from './form-steps/Step3Eligibility';
import Step4Position from './form-steps/Step4Position';
import Step5Employment from './form-steps/Step5Employment';
import Step6Education from './form-steps/Step6Education';
import Step7Certifications from './form-steps/Step7Certifications';
import Step8References from './form-steps/Step8References';
import Step9Certification from './form-steps/Step9Certification';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MultiStepApplicationForm = ({ job, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Personal Information
    first_name: '',
    middle_name: '',
    last_name: '',
    preferred_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    telephone: '',
    email: '',
    date_of_birth: '',
    
    // Step 2: Emergency Contact & Self-ID
    emergency_contact_name: '',
    emergency_relationship: '',
    emergency_phone: '',
    race_ethnicity: '',
    race_other: '',
    
    // Step 3: Pre-Employment Eligibility
    has_driver_license: '',
    license_state_pa: '',
    license_other_state: '',
    has_reliable_transportation: '',
    is_veteran: '',
    has_criminal_conviction: '',
    conviction_details: '',
    willing_background_check: '',
    willing_physical_tb_test: '',
    lived_in_pa_less_2years: '',
    prior_states: '',
    future_commitments: '',
    future_commitments_details: '',
    
    // Step 4: Position & Availability
    position_type: '',
    position_applied: job?.title || '',
    earliest_start_date: '',
    days_available: [],
    preferred_shifts: [],
    weekend_acknowledged: '',
    willing_rotating_shifts: '',
    willing_overtime: '',
    scheduling_notes: '',
    desired_pay_rate: '',
    
    // Step 5: Employment History
    emp1_name: '',
    emp1_address: '',
    emp1_city: '',
    emp1_state: '',
    emp1_zip: '',
    emp1_title: '',
    emp1_dates: '',
    emp1_pay_rate: '',
    emp1_supervisor: '',
    emp1_supervisor_phone: '',
    emp1_reason_leaving: '',
    emp1_may_contact: '',
    
    emp2_name: '',
    emp2_address: '',
    emp2_city: '',
    emp2_state: '',
    emp2_zip: '',
    emp2_title: '',
    emp2_dates: '',
    emp2_pay_rate: '',
    emp2_supervisor: '',
    emp2_supervisor_phone: '',
    emp2_reason_leaving: '',
    emp2_may_contact: '',
    
    emp3_name: '',
    emp3_address: '',
    emp3_city: '',
    emp3_state: '',
    emp3_zip: '',
    emp3_title: '',
    emp3_dates: '',
    emp3_pay_rate: '',
    emp3_supervisor: '',
    emp3_supervisor_phone: '',
    emp3_reason_leaving: '',
    emp3_may_contact: '',
    
    // Step 6: Education
    education_level: '',
    school1_name: '',
    school1_address: '',
    school1_city: '',
    school1_state: '',
    school1_zip: '',
    school1_degree: '',
    school1_major: '',
    school1_dates: '',
    school1_year_completed: '',
    school1_currently_enrolled: 'no',
    
    school2_name: '',
    school2_address: '',
    school2_city: '',
    school2_state: '',
    school2_zip: '',
    school2_degree: '',
    school2_major: '',
    school2_dates: '',
    school2_year_completed: '',
    school2_currently_enrolled: 'no',
    
    school3_name: '',
    school3_address: '',
    school3_city: '',
    school3_state: '',
    school3_zip: '',
    school3_degree: '',
    school3_major: '',
    school3_dates: '',
    school3_year_completed: '',
    school3_currently_enrolled: 'no',
    
    has_human_services_experience: '',
    
    // Step 7: Licenses & Certifications
    has_professional_license: '',
    is_cpr_certified: '',
    certifications_list: '',
    cert_institution: '',
    cert_address: '',
    cert_city: '',
    cert_state: '',
    cert_zip: '',
    
    // Step 8: References
    ref1_name: '',
    ref1_address: '',
    ref1_city: '',
    ref1_state: '',
    ref1_zip: '',
    ref1_email: '',
    ref1_phone: '',
    
    ref2_name: '',
    ref2_address: '',
    ref2_city: '',
    ref2_state: '',
    ref2_zip: '',
    ref2_email: '',
    ref2_phone: '',
    
    ref3_name: '',
    ref3_address: '',
    ref3_city: '',
    ref3_state: '',
    ref3_zip: '',
    ref3_email: '',
    ref3_phone: '',
    
    // Step 9: Certification
    certification_signature: '',
    certification_date: new Date().toISOString().split('T')[0],
    
    // Resume (optional)
    resume_filename: '',
    resume_data: '',
    resume_content_type: ''
  });

  const { toast } = useToast();
  const totalSteps = 9;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxArray = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(v => v !== value)
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/public/applications`, formData);
      toast({
        title: 'Application Submitted!',
        description: 'Your application has been sent to jobs@lhss-email.com. We will review it and contact you soon.',
      });
      onClose();
    } catch (error) {
      let errorMessage = 'Please check all required fields and try again.';
      
      // Parse validation errors from backend
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          const missingFields = detail
            .filter(e => e.type === 'missing')
            .map(e => e.loc?.[1] || 'unknown field')
            .slice(0, 3); // Show first 3 missing fields
          if (missingFields.length > 0) {
            errorMessage = `Missing required fields: ${missingFields.join(', ')}${detail.length > 3 ? '...' : ''}`;
          }
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      
      toast({
        title: 'Submission Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const steps = [
    { number: 1, title: 'Personal Info' },
    { number: 2, title: 'Emergency & ID' },
    { number: 3, title: 'Eligibility' },
    { number: 4, title: 'Position Details' },
    { number: 5, title: 'Employment' },
    { number: 6, title: 'Education' },
    { number: 7, title: 'Certifications' },
    { number: 8, title: 'References' },
    { number: 9, title: 'Certification' }
  ];

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                currentStep > step.number 
                  ? 'bg-green-500 text-white' 
                  : currentStep === step.number 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {currentStep > step.number ? <Check className="w-5 h-5" /> : step.number}
              </div>
              <span className="text-xs mt-1 text-center hidden sm:block">{step.title}</span>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-1 mx-2 ${
                currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
              }`}></div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Form Content */}
      <div className="min-h-[500px]">
        {currentStep === 1 && <Step1PersonalInfo formData={formData} handleChange={handleChange} />}
        {currentStep === 2 && <Step2EmergencyContact formData={formData} handleChange={handleChange} />}
        {currentStep === 3 && <Step3Eligibility formData={formData} handleChange={handleChange} />}
        {currentStep === 4 && <Step4Position formData={formData} handleChange={handleChange} handleCheckboxArray={handleCheckboxArray} />}
        {currentStep === 5 && <Step5Employment formData={formData} handleChange={handleChange} />}
        {currentStep === 6 && <Step6Education formData={formData} handleChange={handleChange} />}
        {currentStep === 7 && <Step7Certifications formData={formData} handleChange={handleChange} />}
        {currentStep === 8 && <Step8References formData={formData} handleChange={handleChange} />}
        {currentStep === 9 && <Step9Certification formData={formData} handleChange={handleChange} />}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          type="button"
          onClick={prevStep}
          disabled={currentStep === 1}
          variant="outline"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        
        {currentStep < totalSteps ? (
          <Button
            type="button"
            onClick={nextStep}
            className="bg-amber-500 hover:bg-amber-600"
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleSubmit}
            className="bg-green-600 hover:bg-green-700"
          >
            Submit Application
          </Button>
        )}
      </div>
    </div>
  );
};

export default MultiStepApplicationForm;
